import { Prose } from "@components/Prose.tsx";
import { typeLabel } from "@components/SchemaTree.tsx";
import { deref } from "@lib/content.ts";
import type { Json, ParameterObject } from "@lib/types.ts";

interface Props {
  version: string;
  title: string;
  parameters: ParameterObject[];
}

export function ParamTable({ version, title, parameters }: Props) {
  if (parameters.length === 0) return null;

  return (
    <section className="mt-6">
      {/* h2: a sibling of "Request body" and "Responses", so heading levels
          stay contiguous under the page h1. */}
      <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">
        {title}
      </h2>
      <dl className="rounded-default border border-line">
        {parameters.map((param) => {
          const schema = param.schema
            ? deref(version, param.schema)
            : undefined;
          return (
            <div
              key={`${param.in}:${param.name}`}
              className="border-b border-line px-3 py-2.5 last:border-b-0"
            >
              <dt className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <code className="font-mono text-[13px] font-semibold text-ink">
                  {param.name}
                </code>
                <span className="font-mono text-[12px] text-accent">
                  {param.schema ? typeLabel(version, param.schema) : "string"}
                </span>
                {param.required ? (
                  <span className="text-[10.5px] font-semibold uppercase tracking-wide text-danger">
                    required
                  </span>
                ) : (
                  <span className="text-[10.5px] uppercase tracking-wide text-subtle">
                    optional
                  </span>
                )}
                {param.deprecated ? (
                  <span className="rounded-sm2 bg-warning-tint px-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-warning">
                    deprecated
                  </span>
                ) : null}
              </dt>
              <dd>
                <Prose
                  size="sm"
                  html={param.descriptionHtml}
                  className="mt-1 text-muted"
                />
                <Facets schema={schema} example={param.example} />
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function Facets({
  schema,
  example,
}: {
  schema: Json | undefined;
  example: unknown;
}) {
  if (!schema && example === undefined) return null;

  const bits: string[] = [];
  if (schema?.default !== undefined) {
    bits.push(`default ${JSON.stringify(schema.default)}`);
  }
  if (example !== undefined) bits.push(`example ${JSON.stringify(example)}`);
  if (schema?.minLength !== undefined)
    bits.push(`min length ${String(schema.minLength)}`);
  if (schema?.minimum !== undefined) bits.push(`min ${String(schema.minimum)}`);
  if (schema?.maximum !== undefined) bits.push(`max ${String(schema.maximum)}`);
  if (schema?.pattern !== undefined)
    bits.push(`pattern ${String(schema.pattern)}`);

  const enumValues = schema?.enum as unknown[] | undefined;

  if (bits.length === 0 && !enumValues?.length) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-subtle">
      {bits.map((bit) => (
        <span key={bit}>{bit}</span>
      ))}
      {enumValues?.length ? (
        <span className="flex flex-wrap items-center gap-1">
          one of
          {enumValues.map((value) => (
            <code
              key={String(value)}
              className="rounded-sm2 bg-surface-2 px-1.5 py-px text-[11px] text-ink"
            >
              {String(value)}
            </code>
          ))}
        </span>
      ) : null}
    </div>
  );
}
